package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.data.dto.MessageDto
import com.arena_web.data.dto.ReactionDto
import com.arena_web.ui.components.cards.ChatPlayerCard
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.Avatar
import com.arena_web.ui.components.widgets.AvatarSize
import com.arena_web.ui.components.widgets.MediaLightbox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons

import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodySMedium
import com.arena_web.ui.theme.bodySRegular
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Video

enum class GroupPosition { SINGLE, FIRST, MIDDLE, LAST }

// ── Static message colors (no sport-dependent theming) ──
private val OwnBubbleColor = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(10, 46, 103)  // #0A2E67
private val OtherBubbleColor = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(9, 26, 64)  // WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40)

private fun ownAlpha(alpha: Double): String = "rgba(255,255,255,$alpha)"

@Composable
fun MessageBubble(
    message: MessageDto,
    isOwnMessage: Boolean,
    isGroupChat: Boolean = false,
    showSenderInfo: Boolean = false,
    groupPosition: GroupPosition = GroupPosition.SINGLE,
    currentUserId: String? = null,
    // Sender's generated player-card frame + cropped avatar — supplied by the
    // chat panel from chat.members. When present, group-chat avatars render
    // as a Highlight-sized PlayerCard instead of the round Avatar tile.
    senderCardImageUrl: String? = null,
    senderCardAvatarUrl: String? = null,
    // Player card data for event chat (replaces avatar)
    usePlayerCard: Boolean = false,
    cardPlayerName: String = "",
    cardAvatarUrl: String? = null,
    cardAssetsBasePath: String? = null,
    cardTextColor: String? = null,
    cardSport: String = "football",
    onEdit: (() -> Unit)? = null,
    onDelete: (() -> Unit)? = null,
    onReaction: ((String) -> Unit)? = null,
    onReply: (() -> Unit)? = null,
    onPin: (() -> Unit)? = null
) {
    val t = LocalAppStrings.current
    var showMenu by remember { mutableStateOf(false) }
    var longPressTimer by remember { mutableStateOf<Int?>(null) }

    if (message.type == "SYSTEM") {
        Box(
            modifier = Modifier.fillMaxWidth().sPadding(4, 0),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                message.content,
                modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
        return
    }

    val topPad = when (groupPosition) {
        GroupPosition.FIRST, GroupPosition.SINGLE -> 6
        else -> 1
    }
    val bottomPad = when (groupPosition) {
        GroupPosition.LAST, GroupPosition.SINGLE -> 4
        else -> 1
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingTop(topPad)
            .sPaddingBottom(bottomPad)
            .then(
                if (isOwnMessage) Modifier.styleModifier { property("justify-content", "flex-end") }
                else Modifier.styleModifier { property("justify-content", "flex-start") }
            ),
        verticalAlignment = Alignment.Bottom
    ) {
        if (usePlayerCard) {
            // Event chat: show player card instead of avatar
            if (!isOwnMessage && showSenderInfo) {
                ChatPlayerCard(
                    playerName = cardPlayerName,
                    sport = cardSport,
                    size = PlayerCardSize.XtraSmall,
                    avatarUrl = cardAvatarUrl ?: message.senderAvatarUrl,
                    cardAssetsBasePath = cardAssetsBasePath,
                    cardTextColor = cardTextColor,
                    modifier = Modifier.sMarginRight(8)
                )
            }
        } else {
            if (!isOwnMessage && isGroupChat && showSenderInfo) {
                if (senderCardImageUrl != null) {
                    // Group chat avatar = sender's player card, matching the
                    // followers list / chat header treatment. Same 40×60
                    // footprint so message bubbles below align identically.
                    Box(
                        modifier = Modifier
                            .sMarginRight(8)
                            .styleModifier {
                                property("flex-shrink", "0")
                                property("width", s(40))
                                property("height", s(60))
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        PlayerCard(
                            playerName = "",
                            rating = 0,
                            position = "",
                            sport = "football",
                            size = PlayerCardSize.Highlight,
                            cardFrameUrl = senderCardImageUrl,
                            avatarUrl = senderCardAvatarUrl ?: message.senderAvatarUrl ?: AppIcons.EmptyAvatar,
                            hideRating = true,
                            hideShadow = true,
                        )
                    }
                } else {
                    Avatar(
                        imageUrl = message.senderAvatarUrl,
                        name = message.senderUsername,
                        size = AvatarSize.SMALL,
                        modifier = Modifier.sMarginRight(8)
                    )
                }
            } else if (!isOwnMessage && isGroupChat) {
                Box(modifier = Modifier.sWidth(40))
            }
        }

        Column(
            modifier = Modifier
                .styleModifier {
                    property("max-width", "70%")
                    property("position", "relative")
                }
        ) {
            // Sender name (hidden when usePlayerCard)
            if (!usePlayerCard && !isOwnMessage && isGroupChat && showSenderInfo) {
                SpanText(
                    message.senderUsername ?: t.chatUnknownSender,
                    modifier = Modifier
                        .bodySMedium()
                        .color(OwnBubbleColor)
                        .sPaddingLeft(12)
                        .sPaddingBottom(2)
                )
            }

            // Bubble
            Box(
                modifier = Modifier
                    .backgroundColor(
                        if (isOwnMessage) OwnBubbleColor
                        else OtherBubbleColor
                    )
                    .then(
                        when (message.type.uppercase()) {
                            "IMAGE", "VIDEO" -> Modifier.sPadding(4, 4)
                            else -> Modifier.sPadding(8, 12)
                        }
                    )
                    .styleModifier {
                        // border-radius: top-left top-right bottom-right bottom-left
                        // "tail" = 0px corner at bottom on sender's side (last/single)
                        val r = s(16)
                        val sm = s(4)
                        val tail = s(0)
                        if (isOwnMessage) {
                            val br = when (groupPosition) {
                                GroupPosition.SINGLE -> "$r $r $tail $r"
                                GroupPosition.FIRST ->  "$r $r $sm $r"
                                GroupPosition.MIDDLE -> "$r $sm $sm $r"
                                GroupPosition.LAST ->   "$r $sm $tail $r"
                            }
                            property("border-radius", br)
                        } else {
                            val br = when (groupPosition) {
                                GroupPosition.SINGLE -> "$r $r $r $tail"
                                GroupPosition.FIRST ->  "$r $r $r $sm"
                                GroupPosition.MIDDLE -> "$sm $r $r $sm"
                                GroupPosition.LAST ->   "$sm $r $r $tail"
                            }
                            property("border-radius", br)
                        }
                        property("position", "relative")
                        property("cursor", "pointer")
                        property("-webkit-user-select", "none")
                        property("user-select", "none")
                    }
                    .attrsModifier {
                        onContextMenu { event ->
                            event.preventDefault()
                            showMenu = true
                        }
                        onTouchStart { _ ->
                            longPressTimer = window.setTimeout({
                                showMenu = true
                            }, 500)
                        }
                        onTouchEnd { _ ->
                            longPressTimer?.let { window.clearTimeout(it) }
                            longPressTimer = null
                        }
                        onTouchMove { _ ->
                            longPressTimer?.let { window.clearTimeout(it) }
                            longPressTimer = null
                        }
                    }
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    // Reply preview
                    if (message.replyToId != null) {
                        ReplyPreview(
                            senderName = message.replyToSenderName,
                            content = message.replyToContent,
                            isOwnMessage = isOwnMessage
                        )
                    }

                    val isMediaType = message.type.uppercase() in listOf("IMAGE", "VIDEO")
                    val isTextType = message.type.uppercase() !in listOf("IMAGE", "VIDEO", "AUDIO", "VOICE", "FILE", "DOCUMENT")
                    val timeStr = ChatTimeUtils.formatMessageTime(message.createdAt)

                    when (message.type.uppercase()) {
                        "IMAGE" -> ImageMessageContent(message, isOwnMessage)
                        "VIDEO" -> VideoMessageContent(message, isOwnMessage)
                        "AUDIO", "VOICE" -> AudioMessageContent(message, isOwnMessage)
                        "FILE", "DOCUMENT" -> FileMessageContent(message, isOwnMessage)
                        else -> TextWithInlineTime(
                            text = message.content,
                            time = timeStr,
                            isEdited = message.isEdited,
                            isOwnMessage = isOwnMessage,
                            isRead = message.isRead
                        )
                    }

                    // Timestamp for audio/file (not text, not media)
                    if (!isMediaType && !isTextType) {
                        MessageTimestamp(
                            time = timeStr,
                            isEdited = message.isEdited,
                            isOwnMessage = isOwnMessage,
                            isRead = message.isRead
                        )
                    }
                }

            }

            // Emoji reactions — overlap the bottom edge
            if (message.reactions.isNotEmpty()) {
                ReactionBar(
                    reactions = message.reactions,
                    isOwnMessage = isOwnMessage,
                    currentUserId = currentUserId,
                    onReaction = onReaction
                )
            }

            // Context menu
            if (showMenu) {
                MessageContextMenu(
                    isOwnMessage = isOwnMessage,
                    onReaction = { emoji ->
                        showMenu = false
                        onReaction?.invoke(emoji)
                    },
                    onReply = {
                        showMenu = false
                        onReply?.invoke()
                    },
                    onPin = {
                        showMenu = false
                        onPin?.invoke()
                    },
                    onEdit = if (isOwnMessage && onEdit != null) {{
                        showMenu = false
                        onEdit()
                    }} else null,
                    onDelete = if (isOwnMessage && onDelete != null) {{
                        showMenu = false
                        onDelete()
                    }} else null,
                    onDismiss = { showMenu = false }
                )
            }

            // Tail (хвостик) — absolute, no layout impact
            if (groupPosition == GroupPosition.LAST || groupPosition == GroupPosition.SINGLE) {
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("bottom", "0")
                            property("width", s(10))
                            property("height", s(10))
                            property("pointer-events", "none")
                            if (isOwnMessage) {
                                property("right", s(-5))
                                property("clip-path", "polygon(0 0, 0 100%, 100% 100%)")
                            } else {
                                property("left", s(-5))
                                property("clip-path", "polygon(100% 0, 0 100%, 100% 100%)")
                            }
                        }
                        .backgroundColor(
                            if (isOwnMessage) OwnBubbleColor
                            else OtherBubbleColor
                        )
                )
            }
        }

        // Own message player card (right side)
        if (usePlayerCard && isOwnMessage) {
            if (showSenderInfo) {
                ChatPlayerCard(
                    playerName = cardPlayerName,
                    sport = cardSport,
                    size = PlayerCardSize.XtraSmall,
                    avatarUrl = cardAvatarUrl ?: message.senderAvatarUrl,
                    cardAssetsBasePath = cardAssetsBasePath,
                    cardTextColor = cardTextColor,
                    modifier = Modifier.sMarginLeft(8)
                )
            }
        }
    }
}

// ── Reply preview inside bubble ──

@Composable
private fun ReplyPreview(
    senderName: String?,
    content: String?,
    isOwnMessage: Boolean
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sMarginBottom(6)
            .sBorderRadius(8)
            .backgroundColor(
                if (isOwnMessage) Color(ownAlpha(0.10))
                else Color("var(--l-hairline, rgba(255,255,255,0.06))")
            )
            .sPadding(8, 10)
            .sGap(10),
        verticalAlignment = Alignment.Top
    ) {
        // Vertical line — use onPrimary so it's visible on any sport bubble
        Box(
            modifier = Modifier
                .sWidth(3)
                .styleModifier {
                    property("align-self", "stretch")
                    property("flex-shrink", "0")
                }
                .sBorderRadius(2)
                .backgroundColor(OwnBubbleColor)
        )

        Column {
            SpanText(
                senderName ?: t.chatUnknownSender,
                modifier = Modifier
                    .bodySMedium()
                    .color(
                        if (isOwnMessage) Color(ownAlpha(0.9))
                        else com.arena_web.ui.theme.ThemeVars.Text1
                    )
            )
            SpanText(
                (content ?: "").take(80) + if ((content?.length ?: 0) > 80) "..." else "",
                modifier = Modifier
                    .bodySRegular()
                    .color(
                        if (isOwnMessage) Color(ownAlpha(0.7))
                        else com.arena_web.ui.theme.ThemeVars.Text2
                    )
                    .sMarginTop(2)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("max-width", s(300))
                    }
            )
        }
    }
}

// ── Text message with inline time ──

@Composable
private fun TextWithInlineTime(
    text: String,
    time: String,
    isEdited: Boolean,
    isOwnMessage: Boolean,
    isRead: Boolean
) {
    val t = LocalAppStrings.current
    val textColor = if (isOwnMessage) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text1
    val timeColor = if (isOwnMessage) ownAlpha(0.6) else "var(--arena-text-3)"
    val editedColor = if (isOwnMessage) ownAlpha(0.5) else "var(--arena-text-3)"

    Span(attrs = {
        style {
            property("display", "flex")
            property("align-items", "flex-end")
            property("gap", s(8))
        }
    }) {
        // Text
        Span(attrs = {
            style {
                property("flex", "1")
                property("min-width", "0")
                property("font-size", s(16))
                property("font-family", "Urbanist, sans-serif")
                property("font-weight", "400")
                property("letter-spacing", "0.032px")
                property("color", textColor.toString())
                property("white-space", "pre-wrap")
                property("word-break", "break-word")
                property("line-height", "1.5")
            }
        }) {
            Text(text)
        }

        // Time — pinned to bottom right
        Span(attrs = {
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", s(3))
                property("font-size", s(11))
                property("line-height", "1")
                property("color", timeColor)
                property("white-space", "nowrap")
                property("flex-shrink", "0")
            }
        }) {
            if (isEdited) {
                Span(attrs = {
                    style {
                        property("font-style", "italic")
                        property("color", editedColor)
                    }
                }) { Text(t.chatEdited) }
            }
            Text(time)
            if (isOwnMessage) {
                ReadCheckmarks(isRead)
            }
        }
    }
}

// ── Image message ──

@Composable
private fun ImageMessageContent(message: MessageDto, isOwnMessage: Boolean) {

    var expanded by remember { mutableStateOf(false) }

    val hasCaption = message.content.isNotBlank() &&
            message.content != message.fileName &&
            message.content.uppercase() != "IMAGE"

    Column(modifier = Modifier.sGap(4)) {
        val imgUrl = message.fileUrl
        if (imgUrl != null) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "relative")
                        property("max-width", s(280))
                    }
                    .cursor(Cursor.Pointer)
                    .onClick { expanded = true }
            ) {
                Image(
                    src = imgUrl,
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("max-width", s(280))
                            property("max-height", s(200))
                            property("border-radius", s(12))
                            property("object-fit", "cover")
                            property("display", "block")
                        }
                )
                // Timestamp overlay on image (only if no caption)
                if (!hasCaption) {
                    MediaTimestampOverlay(
                        time = ChatTimeUtils.formatMessageTime(message.createdAt),
                        isEdited = message.isEdited,
                        isOwnMessage = isOwnMessage,
                        isRead = message.isRead
                    )
                }
            }
        }

        // Caption text if present
        if (hasCaption) {
            SpanText(
                message.content,
                modifier = Modifier
                    .bodyMRegular()
                    .color(if (isOwnMessage) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text1)
                    .sPadding(4, 8)
                    .styleModifier {
                        property("white-space", "pre-wrap")
                        property("word-break", "break-word")
                    }
            )
            MessageTimestamp(
                time = ChatTimeUtils.formatMessageTime(message.createdAt),
                isEdited = message.isEdited,
                isOwnMessage = isOwnMessage,
                isRead = message.isRead
            )
        }
    }

    if (expanded && message.fileUrl != null) {
        MediaLightbox(
            url = message.fileUrl!!,
            isVideo = false,
            onDismiss = { expanded = false }
        )
    }
}

// ── Video message ──

@Composable
private fun VideoMessageContent(message: MessageDto, isOwnMessage: Boolean) {

    var expanded by remember { mutableStateOf(false) }

    val hasCaption = message.content.isNotBlank() &&
            message.content.uppercase() != "VIDEO" &&
            message.content != message.fileName

    Column(modifier = Modifier.sGap(4)) {
        val videoUrl = message.fileUrl
        if (videoUrl != null) {
            // Thumbnail with play overlay
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("max-width", s(280))
                        property("max-height", s(200))
                        property("position", "relative")
                    }
                    .sBorderRadius(12)
                    .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
                    .cursor(Cursor.Pointer)
                    .onClick { expanded = true },
                contentAlignment = Alignment.Center
            ) {
                Video(
                    attrs = {
                        attr("src", videoUrl)
                        attr("preload", "metadata")
                        style {
                            property("max-width", s(280))
                            property("max-height", s(200))
                            property("border-radius", s(12))
                            property("width", "100%")
                            property("pointer-events", "none")
                        }
                    }
                )
                // Play overlay
                var playHovered by remember { mutableStateOf(false) }
                Box(
                    modifier = Modifier
                        .sSize(48)
                        .borderRadius(50.percent)
                        .backgroundColor(
                            if (playHovered) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 200)
                            else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 160)
                        )
                        .attrsModifier {
                            onMouseEnter { playHovered = true }
                            onMouseLeave { playHovered = false }
                        }
                        .styleModifier {
                            property("transform", if (playHovered) "scale(1.1)" else "scale(1)")
                            property("transition", "transform 0.2s ease, background-color 0.2s ease")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        src = AppIcons.Play,
                        modifier = Modifier.sSize(20).styleModifier {
                            property("filter", "brightness(2)")
                        }
                    )
                }
                // Timestamp overlay on video (only if no caption)
                if (!hasCaption) {
                    MediaTimestampOverlay(
                        time = ChatTimeUtils.formatMessageTime(message.createdAt),
                        isEdited = message.isEdited,
                        isOwnMessage = isOwnMessage,
                        isRead = message.isRead
                    )
                }
            }
        }

        // Caption
        if (hasCaption) {
            SpanText(
                message.content,
                modifier = Modifier
                    .bodyMRegular()
                    .color(if (isOwnMessage) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text1)
                    .sPadding(4, 8)
                    .styleModifier {
                        property("white-space", "pre-wrap")
                        property("word-break", "break-word")
                    }
            )
            MessageTimestamp(
                time = ChatTimeUtils.formatMessageTime(message.createdAt),
                isEdited = message.isEdited,
                isOwnMessage = isOwnMessage,
                isRead = message.isRead
            )
        }
    }

    if (expanded && message.fileUrl != null) {
        MediaLightbox(
            url = message.fileUrl!!,
            isVideo = true,
            onDismiss = { expanded = false }
        )
    }
}

// ── Audio/Voice message ──

@Composable
private fun AudioMessageContent(message: MessageDto, isOwnMessage: Boolean) {


    Row(
        modifier = Modifier.sGap(10),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Play button
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(18)
                .backgroundColor(
                    if (isOwnMessage) Color(ownAlpha(0.2))
                    else OwnBubbleColor
                )
                .cursor(Cursor.Pointer),
            contentAlignment = Alignment.Center
        ) {
            Image(
                src = AppIcons.Play,
                modifier = Modifier
                    .sSize(16)
                    .styleModifier {
                        property("filter", "brightness(1)")
                    }
            )
        }

        // Waveform visualization (simplified bars)
        Row(
            modifier = Modifier.sGap(2).styleModifier { property("align-items", "center") }
        ) {
            val bars = listOf(12, 20, 8, 24, 16, 28, 12, 20, 8, 16, 24, 12, 20, 28, 16, 8, 20, 12, 24, 16)
            bars.forEach { height ->
                Box(
                    modifier = Modifier
                        .sWidth(3)
                        .sHeight(height)
                        .sBorderRadius(2)
                        .backgroundColor(
                            if (isOwnMessage) Color(ownAlpha(0.6))
                            else com.arena_web.ui.theme.ThemeVars.Text3
                        )
                )
            }
        }

        // Duration
        SpanText(
            formatDuration(message.duration),
            modifier = Modifier
                .bodySRegular()
                .color(
                    if (isOwnMessage) Color(ownAlpha(0.7))
                    else com.arena_web.ui.theme.ThemeVars.Text3
                )
        )
    }
}

// ── File/Document message ──

@Composable
private fun FileMessageContent(message: MessageDto, isOwnMessage: Boolean) {
    val t = LocalAppStrings.current

    Row(
        modifier = Modifier
            .sGap(12)
            .cursor(Cursor.Pointer)
            .onClick {
                message.fileUrl?.let { kotlinx.browser.window.open(it, "_blank") }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Document icon
        Box(
            modifier = Modifier
                .sSize(40)
                .sBorderRadius(10)
                .backgroundColor(
                    if (isOwnMessage) Color(ownAlpha(0.15))
                    else Color("rgba(255,255,255,0.05)")
                ),
            contentAlignment = Alignment.Center
        ) {
            Image(
                src = AppIcons.Document,
                modifier = Modifier
                    .sSize(22)
                    .styleModifier {
                        if (isOwnMessage) {
                            property("filter", "brightness(1)")
                        } else {
                            property("filter", "brightness(0.7)")
                        }
                    }
            )
        }

        // File name + size
        Column(modifier = Modifier.sGap(2)) {
            SpanText(
                message.fileName ?: t.chatFileFallback,
                modifier = Modifier
                    .bodySMedium()
                    .color(if (isOwnMessage) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text1)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("max-width", s(200))
                    }
            )
            SpanText(
                formatFileSize(message.fileSize),
                modifier = Modifier
                    .bodySRegular()
                    .color(
                        if (isOwnMessage) Color(ownAlpha(0.6))
                        else com.arena_web.ui.theme.ThemeVars.Text3
                    )
            )
        }
    }
}

// ── Reaction bar ──

@Composable
private fun ReactionBar(
    reactions: List<ReactionDto>,
    isOwnMessage: Boolean,
    currentUserId: String?,
    onReaction: ((String) -> Unit)?
) {

    Row(
        modifier = Modifier
            .sGap(4)
            .styleModifier {
                property("margin-top", s(-6))
                property("position", "relative")
                property("z-index", "1")
            }
            .then(
                if (isOwnMessage) Modifier.styleModifier { property("justify-content", "flex-end") }
                else Modifier
            )
    ) {
        reactions.forEach { reaction ->
            val isOwnReaction = currentUserId != null && reaction.userIds.contains(currentUserId)
            var reactionHovered by remember { mutableStateOf(false) }
            Box(
                modifier = Modifier
                    .sBorderRadius(12)
                    .backgroundColor(
                        if (reactionHovered) Color("var(--l-hairline, rgba(255,255,255,0.08))")
                        else AppColors.Dark.SurfaceVariant
                    )
                    .sPadding(2, 8)
                    .styleModifier {
                        if (isOwnReaction) {
                            property("border", "1.5px solid ${OwnBubbleColor}")
                        } else {
                            property("border", "1px solid ${AppColors.Dark.Border}")
                        }
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", s(4))
                        property("transition", "background-color 0.15s ease")
                    }
                    .cursor(Cursor.Pointer)
                    .onClick { onReaction?.invoke(reaction.emoji) }
                    .attrsModifier {
                        onMouseEnter { reactionHovered = true }
                        onMouseLeave { reactionHovered = false }
                    }
            ) {
                Row(
                    modifier = Modifier.sGap(4),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        reaction.emoji,
                        modifier = Modifier.sFontSize(14)
                    )
                    if (reaction.count > 0) {
                        SpanText(
                            reaction.count.toString(),
                            modifier = Modifier.bodySRegular().color(if (isOwnReaction) org.jetbrains.compose.web.css.Color(OwnBubbleColor.toString()) else com.arena_web.ui.theme.ThemeVars.Text3)
                        )
                    }
                }
            }
        }
    }
}

// ── Context menu ──

@Composable
private fun MessageContextMenu(
    isOwnMessage: Boolean,
    onReaction: (String) -> Unit,
    onReply: () -> Unit,
    onPin: () -> Unit,
    onEdit: (() -> Unit)?,
    onDelete: (() -> Unit)?,
    onDismiss: () -> Unit
) {
    val t = LocalAppStrings.current
    var menuVisible by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) { menuVisible = true }

    // Backdrop
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", "998")
            }
            .onClick { onDismiss() }
    )

    Column(
        modifier = Modifier
            .sWidth(200)
            .backgroundColor(AppColors.Dark.Surface)
            .sBorderRadius(12)
            .sPadding(6, 0)
            .styleModifier {
                property("position", "absolute")
                property("bottom", s(40))
                if (isOwnMessage) {
                    property("right", "0")
                } else {
                    property("left", "0")
                }
                property("z-index", "999")
                property("border", "1px solid ${AppColors.Dark.Border}")
                property("box-shadow", "0 4px 16px rgba(0,0,0,0.4)")
                property("opacity", if (menuVisible) "1" else "0")
                property("transform", if (menuVisible) "scale(1) translateY(0)" else "scale(0.95) translateY(4px)")
                property("transition", "opacity 150ms ease-out, transform 150ms ease-out")
            }
    ) {
        // Quick emoji row
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sPadding(6, 10)
                .sGap(4)
                .styleModifier { property("justify-content", "space-around") }
        ) {
            val quickEmojis = listOf("\u2764\uFE0F", "\uD83D\uDC4D", "\uD83D\uDE02", "\uD83D\uDE2E", "\uD83D\uDE22", "\uD83D\uDD25")
            quickEmojis.forEach { emoji ->
                var emojiHovered by remember { mutableStateOf(false) }
                Box(
                    modifier = Modifier
                        .sSize(32)
                        .sBorderRadius(8)
                        .cursor(Cursor.Pointer)
                        .onClick { onReaction(emoji) }
                        .attrsModifier {
                            onMouseEnter { emojiHovered = true }
                            onMouseLeave { emojiHovered = false }
                        }
                        .then(
                            if (emojiHovered) Modifier.backgroundColor(Color("rgba(255,255,255,0.1)"))
                            else Modifier
                        )
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("transform", if (emojiHovered) "scale(1.15)" else "scale(1)")
                            property("transition", "all 0.15s ease")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(emoji, modifier = Modifier.sFontSize(18))
                }
            }
        }

        // Divider
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(1)
                .sMargin(2)
                .backgroundColor(AppColors.Dark.Border)
        )

        // Reply
        ContextMenuItem(
            icon = AppIcons.ArrowLeft,
            label = t.chatMenuReply,
            onClick = onReply
        )

        // Pin
        ContextMenuItem(
            icon = AppIcons.Pin,
            label = t.chatMenuPin,
            onClick = onPin
        )

        // Edit (own only)
        if (onEdit != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(1)
                    .sMargin(2)
                    .backgroundColor(AppColors.Dark.Border)
            )
            ContextMenuItem(
                icon = AppIcons.Pencil,
                label = t.commonEdit,
                onClick = onEdit
            )
        }

        // Delete (own only)
        if (onDelete != null) {
            if (onEdit == null) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(1)
                        .sMargin(2)
                        .backgroundColor(AppColors.Dark.Border)
                )
            }
            ContextMenuItem(
                icon = AppIcons.Trash,
                label = t.commonDelete,
                isDestructive = true,
                onClick = onDelete
            )
        }
    }
}

@Composable
private fun ContextMenuItem(
    icon: String,
    label: String,
    isDestructive: Boolean = false,
    onClick: () -> Unit
) {
    var itemHovered by remember { mutableStateOf(false) }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 12)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .attrsModifier {
                onMouseEnter { itemHovered = true }
                onMouseLeave { itemHovered = false }
            }
            .then(
                if (itemHovered) Modifier.backgroundColor(AppColors.Dark.SurfaceVariant)
                else Modifier
            )
            .styleModifier {
                property("transition", "background-color 0.15s ease")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            src = icon,
            modifier = Modifier
                .sSize(16)
                .styleModifier {
                    if (isDestructive) {
                        property("filter", "brightness(0) saturate(100%) invert(22%) sepia(95%) saturate(7480%) hue-rotate(358deg) brightness(101%) contrast(119%)")
                    } else {
                        property("filter", "brightness(0.6)")
                    }
                }
        )
        SpanText(
            label,
            modifier = Modifier
                .bodyMRegular()
                .color(if (isDestructive) org.jetbrains.compose.web.css.Color(AppColors.Error.toString()) else com.arena_web.ui.theme.ThemeVars.Text1)
                .sPaddingLeft(8)
        )
    }
}

// ── Timestamp overlay on media ──

@Composable
private fun MediaTimestampOverlay(
    time: String,
    isEdited: Boolean,
    isOwnMessage: Boolean,
    isRead: Boolean
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sBorderRadius(8)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 140))
            .sPadding(2, 6)
            .sGap(3)
            .styleModifier {
                property("position", "absolute")
                property("bottom", s(6))
                property("right", s(6))
                property("align-items", "center")
                property("z-index", "2")
            }
    ) {
        if (isEdited) {
            SpanText(
                t.chatEdited,
                modifier = Modifier
                    .sFontSize(10)
                    .color(Color("rgba(255,255,255,0.7)"))
                    .styleModifier { property("font-style", "italic") }
            )
        }
        SpanText(
            time,
            modifier = Modifier
                .sFontSize(10)
                .color(Color("rgba(255,255,255,0.85)"))
        )
        if (isOwnMessage) {
            ReadCheckmarks(isRead, onMedia = true)
        }
    }
}

// ── Read checkmarks ──

@Composable
private fun ReadCheckmarks(isRead: Boolean, onMedia: Boolean = false) {
    Box(
        modifier = Modifier
            .sWidth(14)
            .sHeight(10)
            .styleModifier { property("position", "relative") }
    ) {
        val checkFilter = if (isRead) {
            // Blue checkmarks for read messages
            "brightness(0) saturate(100%) invert(40%) sepia(90%) saturate(1200%) hue-rotate(200deg) brightness(100%)"
        } else {
            if (onMedia) "brightness(2)"
            else "brightness(0) invert(100%) opacity(0.6)"
        }
        Image(
            src = AppIcons.Check,
            modifier = Modifier.sSize(10).styleModifier {
                property("position", "absolute")
                property("left", "0")
                property("top", "0")
                property("filter", checkFilter)
            }
        )
        Image(
            src = AppIcons.Check,
            modifier = Modifier.sSize(10).styleModifier {
                property("position", "absolute")
                property("left", s(4))
                property("top", "0")
                property("filter", checkFilter)
            }
        )
    }
}

// ── Timestamp inside bubble (for audio, file) ──

@Composable
private fun MessageTimestamp(
    time: String,
    isEdited: Boolean,
    isOwnMessage: Boolean,
    isRead: Boolean
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sGap(4)
            .sMarginTop(4)
            .styleModifier {
                property("align-self", "flex-end")
                property("align-items", "center")
            }
    ) {
        if (isEdited) {
            SpanText(
                t.chatEdited,
                modifier = Modifier
                    .sFontSize(11)
                    .color(
                        if (isOwnMessage) Color(ownAlpha(0.5))
                        else com.arena_web.ui.theme.ThemeVars.Text3
                    )
                    .styleModifier { property("font-style", "italic") }
            )
        }
        SpanText(
            time,
            modifier = Modifier
                .sFontSize(11)
                .color(
                    if (isOwnMessage) Color(ownAlpha(0.6))
                    else com.arena_web.ui.theme.ThemeVars.Text3
                )
        )
        if (isOwnMessage) {
            ReadCheckmarks(isRead)
        }
    }
}

// ── Helpers ──

private fun formatDuration(seconds: Int?): String {
    if (seconds == null) return "0:00"
    val mins = seconds / 60
    val secs = seconds % 60
    return "$mins:${secs.toString().padStart(2, '0')}"
}

private fun formatFileSize(bytes: Long?): String {
    if (bytes == null) return ""
    return when {
        bytes < 1024 -> "$bytes B"
        bytes < 1024 * 1024 -> "${(bytes / 1024)} KB"
        else -> {
            val mb = bytes.toDouble() / (1024 * 1024)
            val rounded = (mb * 10).toLong() / 10.0
            "$rounded MB"
        }
    }
}
